import { FC, useState, useEffect } from 'react';
import { useProcessDataContext } from '@datapeace/vms-visitor-models';
import { debounce, useRefValue } from '@datapeace/1up-frontend-web-utils';
import { IErrorResponse, Spin, toast } from '@datapeace/1up-frontend-web-ui';
import { Dropdown, DropdownValue } from './dropdown-dynamic-field';
import { Api } from '@datapeace/vms-visitor-utils';

export interface IPeopleProps {
  name: string;
  value?: DropdownValue;
  onChange: (name: string, value: IPeopleProps['value']) => void;
  placeholder?: string;
  data?: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    peopleGroups?: any;
  };
  disabled?: boolean;
  settings: {
    personSelectionFields?: string[];
  };
}

const pageSize = 10;
const searchDebounceDelay = 400;

interface Option {
  key: string;
  label: string;
  iconSrc?: string;
  isSetManually?: boolean;
}

export const People: FC<IPeopleProps> = ({
  onChange,
  name,
  value,
  placeholder,
  disabled,
  data,
}) => {
  const [options, setOptions] = useState<Option[]>(
    value && (value as { key: string; label: string; value: string })?.key
      ? [
          {
            ...(value as { key: string; label: string; value: string }),
            isSetManually: true,
          },
        ]
      : []
  );
  const [isFetching, setIsFetching] = useState(false);

  const { accessToken } = useProcessDataContext();

  const personId =
    (value && (value as { key: string; label: string; value: string })?.key) ||
    null;
  const shouldCheckIfPersonExists =
    !!personId && options.length && options[0].isSetManually === true;

  const { visitRequestId } = useProcessDataContext();

  const onChangeRef = useRefValue(onChange);
  useEffect(() => {
    // check if person exists
    if (!personId || !shouldCheckIfPersonExists) return;

    const fetchPersonData = async () => {
      setIsFetching(true);

      try {
        if (!visitRequestId) {
          throw new Error('No visit request found!');
        }

        // if person by this id not found i.e deleted, clear the value
        const person = await Api.checkIfPersonExistsInVisitorApp(
          visitRequestId,
          +personId
        );
        if (!person) {
          setIsFetching(false);
          onChangeRef.current(name, '');
          return;
        }

        const { id, firstName, lastName, pictureUrl } = person;
        setOptions([
          {
            key: String(id),
            label: `${firstName} ${lastName}`,
            iconSrc: pictureUrl,
          },
        ]);
      } catch {
        onChangeRef.current(name, '');
      }

      setIsFetching(false);
    };

    fetchPersonData();
  }, [personId, shouldCheckIfPersonExists, onChangeRef, name, visitRequestId]);

  const fetchPeopleFirstPage = debounce(async (searchText?: string) => {
    if (
      !accessToken ||
      !data ||
      !data.peopleGroups ||
      !data.peopleGroups.length
    ) {
      setOptions([]);
      return;
    }

    setIsFetching(true);

    try {
      if (!visitRequestId) {
        throw new Error('No visit request found!');
      }

      const people = await Api.searchPersonInVisitorApp(visitRequestId, {
        page: 1,
        pageSize,
        search: searchText,
        ...(data.peopleGroups === 'all'
          ? { personGroupNone: false }
          : { personGroupIsOr: data.peopleGroups }),
      });

      setOptions(
        people.map(({ id, firstName, lastName, pictureUrl }) => ({
          key: String(id),
          label: `${firstName} ${lastName}`,
          iconSrc: pictureUrl,
        }))
      );
    } catch (err) {
      toast.error(err as IErrorResponse);
    }

    setIsFetching(false);
  }, searchDebounceDelay);

  return (
    <Dropdown
      loading={isFetching}
      name={name}
      value={value}
      onChange={onChange}
      options={options}
      placeholder={placeholder || 'Search for person'}
      disabled={disabled}
      notFoundContent={isFetching ? <Spin size="small" /> : 'No people found!'}
      filterOption={false}
      onSearch={fetchPeopleFirstPage}
      onFocus={() => fetchPeopleFirstPage()}
      showSearch
      labelInValue
    />
  );
};
